import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTruck, faUndo } from '@fortawesome/free-solid-svg-icons';
import { KG_PER_TONNE, isPieceUnit } from '../../utils/lineUnits';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import useDeliveryOrders from '../../hooks/useDeliveryOrders';
import { useTranslation } from 'react-i18next';

const UpdateTruckWeightModal = ({ isOpen, onClose, onSubmit, onResetSuccess, orderId, initialData, loading = false }) => {
    const { t } = useTranslation();
    const strings = t('deliveryOrdersModule.forms.updateTruckWeight', { returnObjects: true });
    const formStrings = t('deliveryOrdersModule.forms.completeTruckLoading', { returnObjects: true });
    const { resetWeightEntries } = useDeliveryOrders();

    const [order, setOrder] = useState(null);
    const [itemsUpdate, setItemsUpdate] = useState([]);
    const [weightEntries, setWeightEntries] = useState([]);
    const [finalTruckWeight, setFinalTruckWeight] = useState('');
    const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
    const [isLocked, setIsLocked] = useState(true);
    const [resetting, setResetting] = useState(false);
    const [resetError, setResetError] = useState(null);
    const [internalRemark, setInternalRemark] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        if (initialData) {
            setOrder(initialData);
            setFinalTruckWeight(initialData.final_truck_weight != null ? String(initialData.final_truck_weight) : '');
            setEmptyTruckWeight(initialData.empty_truck_weight != null ? String(initialData.empty_truck_weight) : '');
            setInternalRemark(initialData.internal_remark || '');
            setIsLocked(true);

            if (Array.isArray(initialData.items)) {
                setItemsUpdate(initialData.items.map((item, index) => ({
                    index,
                    actualPieces: item.actual_pieces != null ? String(item.actual_pieces) : '',
                    perPieceWeight: item.standard_weight != null ? String(item.standard_weight) : (item.per_piece_weight != null ? String(item.per_piece_weight) : ''),
                    remark: item.remark || '',
                })));
            }

            if (Array.isArray(initialData.weight_entries_list)) {
                setWeightEntries(initialData.weight_entries_list);
            }
        }
    }, [initialData, isOpen]);

    const handleItemFieldChange = (index, field, value) => {
        const str = (typeof value === 'string') ? value : String(value);
        setItemsUpdate(prev => prev.map((item, i) => i === index ? { ...item, [field]: str } : item));
    };

    const savedItemStats = useMemo(() => {
        const stats = new Map();
        weightEntries.forEach(entry => {
            if (entry.items_update_details) {
                entry.items_update_details.forEach(detail => {
                    const prev = stats.get(detail.id) || { pieces: 0, weight: 0 };
                    const weightVal = parseFloat(detail.actual_weight || detail.actual_quantity || 0);
                    stats.set(detail.id, {
                        pieces: prev.pieces + (detail.actual_pieces || 0),
                        weight: prev.weight + weightVal,
                    });
                });
            } else {
                const theoreticalSum = order?.items
                    ?.filter(i => entry.item_ids.includes(i.id))
                    ?.reduce((sum, item) => {
                        const idx = order.items.findIndex(it => it.id === item.id);
                        const pWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || parseFloat(item.standard_weight) || parseFloat(item.per_piece_weight) || 0;
                        return sum + ((item.actual_pieces || 0) * pWeight);
                    }, 0) || 0;

                entry.item_ids.forEach(id => {
                    const prev = stats.get(id) || { pieces: 0, weight: 0 };
                    const item = order?.items?.find(i => i.id === id);
                    const idx = order?.items?.findIndex(i => i.id === id);
                    const pWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || parseFloat(item?.standard_weight) || parseFloat(item?.per_piece_weight) || 0;
                    const pieces = item?.actual_pieces || 0;
                    const ratio = theoreticalSum > 0 ? (pieces * pWeight) / theoreticalSum : (1 / entry.item_ids.length);
                    stats.set(id, {
                        pieces: prev.pieces + pieces,
                        weight: prev.weight + (entry.weight_diff * ratio),
                    });
                });
            }
        });
        return stats;
    }, [weightEntries, order, itemsUpdate]);

    const calculatedData = useMemo(() => {
        if (!order || !order.items || itemsUpdate.length === 0) return { items: [], totalCost: 0, isValid: false };

        const emptyW = parseFloat(emptyTruckWeight) || 0;
        const finalW = parseFloat(finalTruckWeight) || 0;

        const itemCalcs = order.items.map((item, idx) => {
            const currentActualPieces = parseFloat(itemsUpdate[idx]?.actualPieces) || 0;
            const perPieceWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || parseFloat(item.standard_weight) || parseFloat(item.per_piece_weight) || 0;
            const sLength = (parseFloat(item.length) || parseFloat(item.standard_length)) || 0;
            const isMeter = item.unit?.toLowerCase() === 'm';
            const isTonne = item.unit?.toLowerCase() === 'mts';
            const isPiece = isPieceUnit(item.unit?.toLowerCase());
            const theoreticalW = currentActualPieces * perPieceWeight;
            // actualQty is in the LINE'S OWN unit so it compares against
            // item.quantity; theoreticalW is kilograms. A piece line is priced
            // per piece, so its quantity is the piece count — not the weight.
            const actualQty = isMeter
                ? (currentActualPieces * sLength)
                : isTonne
                    ? theoreticalW / KG_PER_TONNE
                    : isPiece
                        ? currentActualPieces
                        : theoreticalW;
            return { ...item, actualQty, theoreticalW, isMeter, isTonne, isPiece };
        });

        let lastTruckWeight = emptyW;
        if (weightEntries.length > 0) {
            lastTruckWeight = Math.max(...weightEntries.map(e => e.truck_weight));
        }
        const currentWeightDiff = finalW > 0 ? Math.max(0, finalW - lastTruckWeight) : 0;
        const currentTheoreticalSum = itemCalcs.reduce((sum, item) => sum + item.theoreticalW, 0);
        const adjustmentFactor = (currentWeightDiff > 0 && currentTheoreticalSum > 0)
            ? currentWeightDiff / currentTheoreticalSum : 1;

        const loadingCostPerKg = parseFloat(order.loading_cost_per_kg) || 0;
        let totalCost = 0;

        const finalItems = itemCalcs.map((item, index) => {
            const currentActualPieces = parseFloat(itemsUpdate[index]?.actualPieces) || 0;
            const savedStats = savedItemStats.get(item.id) || { pieces: 0, weight: 0 };

            let currentAdjustedW = 0;
            if (finalW > 0) {
                currentAdjustedW = item.theoreticalW * adjustmentFactor;
            } else if (item.theoreticalW > 0) {
                currentAdjustedW = item.theoreticalW;
            }

            const totalAdjustedW = savedStats.weight + currentAdjustedW;
            const totalActualPieces = savedStats.pieces + currentActualPieces;
            const cost = totalAdjustedW * loadingCostPerKg;
            totalCost += cost;

            const sLength = (parseFloat(item.length) || parseFloat(item.standard_length)) || 0;
            const isMeter = item.unit?.toLowerCase() === 'm';
            const isTonne = item.unit?.toLowerCase() === 'mts';
            const isPiece = isPieceUnit(item.unit?.toLowerCase());
            let prevDelivered = parseFloat(item.total_delivered_quantity);
            if (isNaN(prevDelivered) || prevDelivered === 0) prevDelivered = parseFloat(item.actual_quantity);
            if (isNaN(prevDelivered) || prevDelivered === 0) prevDelivered = isMeter
                ? (savedStats.pieces * sLength)
                : isTonne
                    ? savedStats.weight / KG_PER_TONNE
                    : isPiece
                        ? savedStats.pieces
                        : savedStats.weight;
            prevDelivered = prevDelivered || 0;

            const totalTarget = parseFloat(item.total_quantity) || parseFloat(item.quantity) || 0;
            const totalDelivered = prevDelivered + (item.actualQty || 0);

            return {
                ...item,
                totalTarget,
                totalDelivered,
                savedPieces: savedStats.pieces,
                savedWeight: savedStats.weight,
                totalActualPieces,
                adjustedW: currentAdjustedW,
                totalAdjustedW,
                cost,
            };
        });

        const totalActualQty = finalItems.reduce((sum, item) => sum + (item.totalAdjustedW || 0), 0);
        return { items: finalItems, totalCost, totalActualQty, isValid: true };
    }, [order, itemsUpdate, finalTruckWeight, emptyTruckWeight, weightEntries, savedItemStats]);

    const handleReset = async () => {
        setResetting(true);
        setResetError(null);
        try {
            const result = await resetWeightEntries(orderId);
            if (result.success) {
                onClose();
                onResetSuccess?.();
            } else {
                setResetError(result.error || 'Failed to reset');
            }
        } catch (err) {
            setResetError(err?.message || 'Failed to reset');
        } finally {
            setResetting(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            empty_truck_weight: Number(emptyTruckWeight) || 0,
            final_truck_weight: Number(finalTruckWeight) || 0,
            internal_remark: internalRemark || '',
        };
        onSubmit(orderId, payload);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        >
            <div
                className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
                style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-6 border-b shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className="p-2.5 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1), color: UI_COLORS.BRAND.PRIMARY }}
                        >
                            <FontAwesomeIcon icon={faTruck} className="text-xl" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {strings.title}
                            </h2>
                            <p className="mt-1 text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>
                                Press Reset to update the values
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors hover:opacity-80"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto grow">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div
                                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
                            />
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {strings.buttons.updating}
                            </p>
                        </div>
                    ) : order ? (
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-6">
                                {/* Order Info + Truck Weights */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <label
                                            className="block text-sm font-semibold mb-2"
                                            style={{ color: UI_COLORS.FORMS.LABEL }}
                                        >
                                            {formStrings.labels.orderId}
                                        </label>
                                        <div
                                            className="w-full px-4 py-3 rounded-xl border-2"
                                            style={{
                                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                color: UI_COLORS.FORMS.INPUT.text,
                                            }}
                                        >
                                            {order.order_number || order.id}
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            className="block text-sm font-semibold mb-2"
                                            style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                        >
                                            {strings.labels.emptyWeight} <span>*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={emptyTruckWeight}
                                            onChange={(e) => setEmptyTruckWeight(e.target.value)}
                                            disabled={isLocked}
                                            className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            style={{
                                                backgroundColor: isLocked ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                color: isLocked ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                            }}
                                            placeholder={strings.placeholders.emptyWeight}
                                            step="0.01"
                                            min="0"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label
                                            className="block text-sm font-semibold mb-2"
                                            style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                        >
                                            {strings.labels.finalWeight} <span>*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={finalTruckWeight}
                                            onChange={(e) => setFinalTruckWeight(e.target.value)}
                                            disabled={isLocked}
                                            className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            style={{
                                                backgroundColor: isLocked ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                color: isLocked ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                            }}
                                            placeholder={strings.placeholders.finalWeight}
                                            step="0.01"
                                            min="0"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                                    <textarea
                                        value={internalRemark}
                                        onChange={(e) => setInternalRemark(e.target.value)}
                                        disabled={isLocked}
                                        className="w-full px-3 py-2 rounded-xl border-2 resize-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        rows="2"
                                        placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                        style={{ backgroundColor: isLocked ? '#f8fafc' : '#f0fdf4', borderColor: '#86efac' }}
                                    />
                                </div>

                                {/* Weight Entries Display */}
                                {weightEntries.length > 0 && (
                                    <div
                                        className="p-4 rounded-xl border"
                                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.05) }}
                                    >
                                        <h4 className="font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                            {formStrings.multiStep?.weightEntriesTitle || 'Weight Entries'} ({weightEntries.length}/10)
                                        </h4>
                                        {weightEntries.map((entry) => {
                                            const itemNames = order.items
                                                .filter(i => entry.item_ids.includes(i.id))
                                                .map(i => i.size_name || i.product_name)
                                                .join(', ');

                                            const totalPieces = entry.items_update_details
                                                ? entry.items_update_details.reduce((sum, d) => sum + (d.actual_pieces || 0), 0)
                                                : order.items.filter(i => entry.item_ids.includes(i.id)).reduce((sum, i) => sum + (i.actual_pieces || 0), 0);
                                            const adjPerPiece = totalPieces > 0 ? entry.weight_diff / totalPieces : 0;

                                            return (
                                                <div key={entry.entry_id} className="p-3 bg-white rounded-lg mb-2 border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                            {formStrings.multiStep?.stepLabel || 'Step'} {entry.entry_id}: {entry.truck_weight} kg
                                                        </span>
                                                        <span className="font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                                            +{entry.weight_diff.toFixed(2)} kg
                                                        </span>
                                                    </div>
                                                    <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                        {entry.item_ids.length} {formStrings.multiStep?.itemsCountLabel || 'items'}: {itemNames}
                                                    </div>
                                                    <div className="text-xs mt-1 font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                        Adjusted per piece weight: {adjPerPiece.toFixed(3)} kg/pc ({totalPieces} pcs)
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Items Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3
                                            className="text-lg font-semibold"
                                            style={{ color: UI_COLORS.TEXT.PRIMARY }}
                                        >
                                            {formStrings.labels.items}
                                        </h3>
                                        {isLocked && (
                                            <button
                                                type="button"
                                                onClick={handleReset}
                                                disabled={resetting}
                                                className="text-sm px-3 py-1 rounded-lg transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                                            >
                                                <FontAwesomeIcon icon={faUndo} className="mr-1" />
                                                {resetting ? t('common.buttons.resetting') : (formStrings.multiStep?.resetAll || 'Reset All')}
                                            </button>
                                        )}
                                        {resetError && (
                                            <span className="text-xs font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{resetError}</span>
                                        )}
                                    </div>

                                    {!Array.isArray(order.items) || order.items.length === 0 ? (
                                        <div className="text-center py-8 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                            {formStrings.messages.noItems}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {calculatedData.items.map((item, index) => {
                                                const isItemLocked = isLocked || item.savedPieces > 0;

                                                return (
                                                    <div
                                                        key={item.id || index}
                                                        className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 rounded-xl border-2 transition-all"
                                                        style={{
                                                            backgroundColor: isItemLocked ? '#f0f9ff' : UI_COLORS.BACKGROUNDS.SECTION,
                                                            borderColor: isItemLocked ? '#bae6fd' : UI_COLORS.BORDERS.LIGHT,
                                                            opacity: isItemLocked ? 0.9 : 1,
                                                        }}
                                                    >
                                                        {/* Product Name */}
                                                        <div className="md:col-span-2">
                                                            <label
                                                                className="text-xs font-semibold uppercase tracking-wider mb-2 block"
                                                                style={{ color: UI_COLORS.TEXT.MUTED }}
                                                            >
                                                                {formStrings.labels.productName}
                                                            </label>
                                                            <div className="flex flex-col">
                                                                <div className="text-sm font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {item.size_name || item.product_name || formStrings.labels.unknownProduct}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {formStrings.labels.unitLabel}: {item.unit}, {formStrings.labels.lengthLabel}: {(parseFloat(item.length) || parseFloat(item.standard_length)) || 0} {item.unit}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Quantity (view-only) */}
                                                        <div>
                                                            <label className="text-sm font-medium mb-2 block" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                                                {formStrings.labels.quantity}
                                                            </label>
                                                            <div
                                                                className="w-full px-3 py-2 rounded-lg border bg-gray-50 text-gray-700 font-medium"
                                                                style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                                                            >
                                                                {item.totalTarget} {item.unit}
                                                            </div>
                                                        </div>

                                                        {/* Actual Pieces */}
                                                        <div>
                                                            <label
                                                                className="block text-sm font-medium mb-2"
                                                                style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                                            >
                                                                {formStrings.labels.actualPieces} <span>*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                pattern="[0-9]*"
                                                                value={isItemLocked ? (item.actual_pieces ?? '') : (itemsUpdate[index]?.actualPieces ?? '')}
                                                                onChange={(e) => handleItemFieldChange(index, 'actualPieces', e.target.value)}
                                                                disabled={isItemLocked}
                                                                className="w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                                style={{
                                                                    backgroundColor: isItemLocked ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                                    borderColor: UI_COLORS.FORMS.INPUT.border,
                                                                    color: isItemLocked ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                                                }}
                                                                placeholder="0"
                                                            />
                                                        </div>

                                                        {/* Per Piece Weight */}
                                                        <div>
                                                            <label
                                                                className="block text-sm font-medium mb-2"
                                                                style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                                            >
                                                                {formStrings.labels.perPieceWeight} <span>*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                pattern="[0-9]*[.,]?[0-9]*"
                                                                value={isItemLocked ? (item.standard_weight ?? '') : (itemsUpdate[index]?.perPieceWeight ?? '')}
                                                                onChange={(e) => handleItemFieldChange(index, 'perPieceWeight', e.target.value)}
                                                                disabled={isItemLocked}
                                                                className="w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                                style={{
                                                                    backgroundColor: isItemLocked ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                                    borderColor: UI_COLORS.FORMS.INPUT.border,
                                                                    color: isItemLocked ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                                                }}
                                                                placeholder="0.00"
                                                            />
                                                        </div>

                                                        {/* Remark */}
                                                        <div className="md:col-span-4">
                                                            <label className="block text-sm font-medium mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                                                {formStrings.labels.remark}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={itemsUpdate[index]?.remark ?? ''}
                                                                onChange={(e) => handleItemFieldChange(index, 'remark', e.target.value)}
                                                                disabled={isItemLocked}
                                                                className="w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                                style={{
                                                                    backgroundColor: isItemLocked ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                                    borderColor: UI_COLORS.FORMS.INPUT.border,
                                                                    color: isItemLocked ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                                                }}
                                                                placeholder={formStrings.placeholders.remark}
                                                            />
                                                        </div>

                                                        {/* Calculated Stats */}
                                                        <div className="md:col-span-6 flex flex-col justify-center bg-white p-3 rounded border text-sm">
                                                            {(() => {
                                                                const calcData = calculatedData.items[index];
                                                                if (!calcData) return null;

                                                                const currentPieces = parseFloat(itemsUpdate[index]?.actualPieces) || 0;
                                                                const lcPerKg = parseFloat(order?.loading_cost_per_kg) || 0;

                                                                // When locked: use API-stored actual values; when editing: use calculated values
                                                                const adjustedWeight = isItemLocked
                                                                    ? (parseFloat(item.actual_weight) || item.savedWeight || 0)
                                                                    : (calcData.adjustedW || 0);
                                                                const adjPiecesCount = isItemLocked
                                                                    ? (parseFloat(item.actual_pieces) || item.savedPieces || 0)
                                                                    : currentPieces;
                                                                const adjPerPiece = adjPiecesCount > 0 ? adjustedWeight / adjPiecesCount : null;
                                                                const displayCost = isItemLocked
                                                                    ? adjustedWeight * lcPerKg
                                                                    : (calcData.cost || 0);

                                                                return (
                                                                    <>
                                                                        <div className="flex justify-between font-bold mt-1" style={{ color: UI_COLORS.TEXT.BLACK }}>
                                                                            <span>{t('common.formFields.labels.totalLoaded')}</span>
                                                                            <span>{calcData.totalDelivered?.toFixed(3)} {item.unit}</span>
                                                                        </div>
                                                                        <hr className="my-2 border-gray-100" />
                                                                        <div className="flex justify-between mt-1">
                                                                            <span className="text-gray-500">{t('common.formFields.labels.adjustedPerPieceWeight')}</span>
                                                                            <span className="font-medium">{adjPerPiece !== null ? `${adjPerPiece.toFixed(3)} kg/pc` : '— kg/pc'}</span>
                                                                        </div>
                                                                        <div className="flex justify-between mt-1" style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                                                            <span>{t('common.formFields.labels.adjustedWeight')}</span>
                                                                            <span className="font-medium">{adjustedWeight.toFixed(2)} kg</span>
                                                                        </div>
                                                                        <div className="flex justify-between mt-1">
                                                                            <span className="text-gray-500">{t('common.formFields.labels.loadingCost')}</span>
                                                                            <span className="font-medium text-gray-900">{t('common.symbols.currency')} {displayCost.toFixed(2)}</span>
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <div className="flex justify-end mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                                <div className="text-right">
                                                    <div className="text-sm text-gray-500">{formStrings.labels.totalLoadingCost}</div>
                                                    <div className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>
                                                        {t('common.symbols.currency')} {isLocked
                                                            ? parseFloat(order?.total_loading_cost || 0).toFixed(2)
                                                            : calculatedData.totalCost.toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </form>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {formStrings.messages.invalidOrder}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpdateTruckWeightModal;
